import { useContext } from 'react'
import swal from 'sweetalert2'

import {
  Box, Button, Columns, LoadingModule, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import useProcessChecklist from './hooks/useProcessChecklist'

import Assets from './components/Assets'
import ChecklistScore from './components/ChecklistScore'
import ChecklistUser from './components/ChecklistUser'
import FailedTaskListItems from './components/FailedTaskListItems'
import Links from './components/Links'
import TaskListItems from './components/TaskListItems'

const MODAL_KEY = 'ViewAndProcessChecklistModal'

type UseProcessChecklistPayload = ReturnType<typeof useProcessChecklist>

type ModalCallbacks = {
  closeModal: () => void,
  toggleTaskListCompletion: (payload: HandleCallbackActionParams) => void,
}

type ConfirmCompletionParams = {
  callbacks: ModalCallbacks,
  submitChecklistFn: UseProcessChecklistPayload['callbacks']['submitChecklist'],
}

const confirmCompletion = (params: ConfirmCompletionParams) => {
  const { callbacks, submitChecklistFn } = params
  const { closeModal, toggleTaskListCompletion } = callbacks

  const toggleTaskListCompletionPayload = {
    callbacks: {
      action: submitChecklistFn,
      afterAction: closeModal,
    },
    toastText: 'Checklist Updated',
  }

  swal
    .fire({
      confirmButtonColor: '#43ac6a',
      confirmButtonText: 'Yes, submit it.',
      icon: 'success',
      showCancelButton: true,
      text: 'Are you sure?',
      title: 'Submit Checklist?',
    })
    .then(({ value }) => {
      if (value){
        toggleTaskListCompletion(toggleTaskListCompletionPayload)
      }
    })
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const ViewAndProcessChecklistModal = (props: ModalProps) => {
  const { callbacks, showModal } = props

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = digObject(modalData, MODAL_KEY, {})
  const { taskList: initTaskList } = modalPayload

  const processChecklistPayload = useProcessChecklist({ taskListId: initTaskList?.id })
  const {
    activeStepKey,
    callbacks: { goToStep, submitChecklist: submitChecklistFn },
    loadingTaskListItems,
    taskList,
    taskList: { completed },
    taskListPayload: {
      updating,
    },
  } = processChecklistPayload

  return (
    <SidebarModal callbacks={callbacks} disableAnimation modalKey={MODAL_KEY} size="fullscreen" showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Process" titleSecondLine="Checklist" />

      <SidebarModal.Content boxProps={{ backgroundColor: 'backgroundColor' }} hasFooter={!completed}>
        <Columns flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <LoadingModule loading={loadingTaskListItems} times={3} />

            {activeStepKey === 'overview' && <TaskListItems processChecklistPayload={processChecklistPayload} />}
            {activeStepKey === 'summary' && <FailedTaskListItems processChecklistPayload={processChecklistPayload} />}
          </Columns.Main>

          <Columns.Sidebar>
            <ChecklistScore processChecklistPayload={processChecklistPayload} />
            {completed && <ChecklistUser taskList={taskList} />}
            <Assets taskList={taskList} />
            <Links taskList={taskList} />
          </Columns.Sidebar>
        </Columns>
      </SidebarModal.Content>

      {!completed && (
        <SidebarModal.Footer>
          <Box marginLeft="auto" width="auto">
            {activeStepKey === 'summary' && (
              <>
                <Button
                  buttonStyle="secondaryEdit"
                  loadingBubbleColor="white"
                  onClick={() => goToStep('overview')}
                  size="large"
                  width="auto"
                >
                  Go Back
                </Button>

                <Button
                  buttonStyle="primaryCreate"
                  loadingBubbleColor="white"
                  loading={updating}
                  onClick={() => confirmCompletion({ callbacks, submitChecklistFn })}
                  marginLeft="medium"
                  size="large"
                  width="auto"
                >
                  Submit Checklist
                </Button>
              </>
            )}

            {activeStepKey === 'overview' && (
              <Button
                buttonStyle="primaryEdit"
                loadingBubbleColor="white"
                onClick={() => goToStep('summary')}
                size="large"
                width="auto"
              >
                Next Step
              </Button>
            )}
          </Box>
        </SidebarModal.Footer>
      )}
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <ViewAndProcessChecklistModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
